import { useEffect, useRef } from 'react';
import { useLeafletContext } from '@react-leaflet/core';
import * as L from 'leaflet';

interface MapBackgroundDimmerProps {
  minZoomForNoDim?: number; // at or above this zoom, opacity -> 0
  maxDimOpacity?: number;   // opacity at or below low zoom
  lowZoomThreshold?: number; // at or below this zoom, opacity -> max
}

// Adds a white overlay over the whole world that becomes stronger when zoomed out
// and fades out as you zoom in. This reduces background clutter at low zooms.
export default function MapBackgroundDimmer({
  minZoomForNoDim = 8,
  maxDimOpacity = 0.6,
  lowZoomThreshold = 4,
}: MapBackgroundDimmerProps) {
  const { map } = useLeafletContext();
  const layerRef = useRef<L.LayerGroup>(new L.LayerGroup());
  const rectRef = useRef<L.Rectangle | null>(null);

  useEffect(() => {
    const layer = layerRef.current;
    const paneName = 'background-dimmer-pane';
    if (!map.getPane(paneName)) {
      map.createPane(paneName);
    }
    const pane = map.getPane(paneName);
    if (pane) {
      pane.style.zIndex = '300'; // above tiles, below data overlays/masks
      pane.style.pointerEvents = 'none';
    }

    map.addLayer(layer);

    const worldBounds: L.LatLngBoundsExpression = [
      [-90, -180],
      [90, 180],
    ];

    const computeOpacity = (z: number) => {
      if (z >= minZoomForNoDim) return 0;
      if (z <= lowZoomThreshold) return maxDimOpacity;
      // linear fade between thresholds
      const t = (z - lowZoomThreshold) / (minZoomForNoDim - lowZoomThreshold);
      return maxDimOpacity * (1 - Math.min(1, Math.max(0, t)));
    };

    const initialOpacity = computeOpacity(map.getZoom());
    const rect = L.rectangle(worldBounds, {
      color: '#ffffff',
      weight: 0,
      fill: true,
      fillColor: '#ffffff',
      fillOpacity: initialOpacity,
      interactive: false,
      pane: paneName,
    });
    rectRef.current = rect;
    layer.addLayer(rect);

    const onZoom = () => {
      const z = map.getZoom();
      const op = computeOpacity(z);
      rect.setStyle({ fillOpacity: op });
    };
    map.on('zoomend', onZoom);

    return () => {
      map.off('zoomend', onZoom);
      map.removeLayer(layer);
      rectRef.current = null;
    };
  }, [map, minZoomForNoDim, maxDimOpacity, lowZoomThreshold]);

  return null;
}
